import { useState } from 'react';
import { Button } from '@launchpad-ui/components';

import { Card, ErrorMessage, Field, PageHeader, StatusBadge } from '../components/ui';
import { useReviewers } from '../lib/hooks/useReviewers';
import {
  type SpotcheckInput,
  useCreateSpotcheck,
  useDeleteSpotcheck,
  useSpotchecks,
  useUpdateSpotcheck,
} from '../lib/hooks/useSpotchecks';

import styles from '../styles/common.module.css';

const EMPTY: { ticket_id: string; ticket_date: string; tse_name: string } = {
  ticket_id: '',
  ticket_date: '',
  tse_name: '',
};

function previewReviewer(ticketId: string, reviewers: string[]): string | null {
  if (!reviewers.length || !ticketId) return null;
  const parsed = parseInt(ticketId, 10);
  if (Number.isNaN(parsed)) return null;
  return reviewers[parsed % reviewers.length];
}

export function Spotchecks() {
  const spotchecksQuery = useSpotchecks();
  const reviewersQuery = useReviewers();
  const create = useCreateSpotcheck();
  const update = useUpdateSpotcheck();
  const remove = useDeleteSpotcheck();

  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'complete' | 'all'>('pending');

  const spotchecks = spotchecksQuery.data ?? [];
  const reviewerNames = (reviewersQuery.data ?? []).filter((r) => r.active).map((r) => r.name);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const ticketId = parseInt(form.ticket_id, 10);
    if (Number.isNaN(ticketId)) {
      setError('Ticket ID must be a number');
      return;
    }
    const payload: SpotcheckInput = {
      ticket_id: ticketId,
      ticket_date: form.ticket_date || undefined,
      tse_name: form.tse_name || undefined,
    };
    try {
      await create.mutateAsync(payload);
      setForm(EMPTY);
      setShowForm(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const filtered = spotchecks.filter((sc) =>
    filter === 'all' ? true : filter === 'pending' ? !sc.complete : sc.complete,
  );
  const pending = spotchecks.filter((sc) => !sc.complete).length;
  const done = spotchecks.filter((sc) => sc.complete).length;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Spotchecks"
        subtitle={`${pending} pending · ${done} complete`}
        actions={
          <Button
            onPress={() => {
              setShowForm((v) => !v);
              setError(null);
            }}
          >
            {showForm ? 'Cancel' : '+ Assign Spotcheck'}
          </Button>
        }
      />

      {showForm && (
        <form onSubmit={submit}>
          <Card>
            <h2 className={styles.sectionTitle}>Assign a Spotcheck</h2>
            <p
              style={{
                fontSize: '0.75rem',
                color: 'var(--lp-color-text-ui-secondary)',
                marginBottom: '1rem',
              }}
            >
              Reviewer is assigned automatically by ticket ID.
            </p>
            <ErrorMessage message={error} />
            <div className={styles.formGrid}>
              <Field label="Ticket ID" required>
                <input
                  className={styles.input}
                  type="number"
                  required
                  value={form.ticket_id}
                  onChange={(e) => setForm((f) => ({ ...f, ticket_id: e.target.value }))}
                  placeholder="e.g. 23757"
                />
              </Field>
              <Field label="TSE Name">
                <input
                  className={styles.input}
                  type="text"
                  value={form.tse_name}
                  onChange={(e) => setForm((f) => ({ ...f, tse_name: e.target.value }))}
                  placeholder="e.g. Alfred"
                />
              </Field>
              <Field label="Ticket Date">
                <input
                  className={styles.input}
                  type="date"
                  value={form.ticket_date}
                  onChange={(e) => setForm((f) => ({ ...f, ticket_date: e.target.value }))}
                />
              </Field>
            </div>
            {form.ticket_id && previewReviewer(form.ticket_id, reviewerNames) && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', fontWeight: 500 }}>
                Will be assigned to: {previewReviewer(form.ticket_id, reviewerNames)}
              </p>
            )}
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" isDisabled={create.isPending}>
                {create.isPending ? 'Assigning…' : 'Assign'}
              </Button>
            </div>
          </Card>
        </form>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {(['pending', 'complete', 'all'] as const).map((f) => (
          <Button key={f} size="small" variant={filter === f ? 'primary' : 'minimal'} onPress={() => setFilter(f)}>
            {f}
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty}>{filter === 'pending' ? 'No pending spotchecks.' : 'Nothing here yet.'}</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Ticket ID</th>
                <th className={styles.th}>TSE</th>
                <th className={styles.th}>Ticket Date</th>
                <th className={styles.th}>Assigned Reviewer</th>
                <th className={styles.th}>Assigned</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((sc) => (
                <tr key={sc.id} className={sc.complete ? styles.dimmed : ''}>
                  <td className={styles.td}>#{sc.ticket_id}</td>
                  <td className={styles.td}>{sc.tse_name || '—'}</td>
                  <td className={styles.td}>{sc.ticket_date ? new Date(sc.ticket_date).toLocaleDateString() : '—'}</td>
                  <td className={styles.td}>{sc.assigned_reviewer}</td>
                  <td className={styles.td}>{new Date(sc.assigned_date).toLocaleDateString()}</td>
                  <td className={styles.td}>
                    <StatusBadge status={sc.complete ? 'completed' : 'pending'} />
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actionsCell}>
                      <Button
                        size="small"
                        variant="minimal"
                        onPress={() =>
                          update.mutate({
                            id: sc.id,
                            patch: { complete: sc.complete ? 0 : 1 },
                          })
                        }
                      >
                        {sc.complete ? 'Reopen' : 'Mark Done'}
                      </Button>
                      <Button
                        size="small"
                        variant="minimal"
                        onPress={() => {
                          if (window.confirm('Remove this spotcheck?')) {
                            remove.mutate(sc.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
